import { Laptop, Moon, Sun } from '@attive/ui/icons'
import { getFormProps, useForm } from '@conform-to/react'
import { parseWithZod } from '@conform-to/zod'
import { invariantResponse } from '@epic-web/invariant'
import { createContext, type ReactNode, useContext } from 'react'
import {
	type ActionFunctionArgs,
	data,
	redirect,
	useFetcher,
	useFetchers,
} from 'react-router'
import { ServerOnly } from 'remix-utils/server-only'
import { z } from 'zod'
import { useHints } from '#app/utils/client-hints.tsx'
import { useRequestInfo } from '#app/utils/request-info.ts'
import { setTheme, type Theme } from '#app/utils/theme.server.ts'

type FormTheme = Theme | 'system'

const ThemeFormSchema = z.object({
	theme: z.enum(['system', 'light', 'dark']),
	// this is useful for progressive enhancement
	redirectTo: z.string().optional(),
})

// Theme context for overriding theme in specific parts of the app
const ThemeContext = createContext<Theme | undefined>(undefined)

export function ThemeProvider({
	theme,
	children,
}: {
	theme: Theme
	children: ReactNode
}) {
	// Determine the CSS class needed for Tailwind theming
	const themeClass = theme === 'dark' ? 'dark' : 'light'

	return (
		<div className={themeClass}>
			<ThemeContext.Provider value={theme}>{children}</ThemeContext.Provider>
		</div>
	)
}

export function useThemeContext() {
	return useContext(ThemeContext)
}

export async function action({ request }: ActionFunctionArgs) {
	const formData = await request.formData()
	const submission = parseWithZod(formData, {
		schema: ThemeFormSchema,
	})

	invariantResponse(submission.status === 'success', 'Invalid theme received')

	const { theme, redirectTo } = submission.value

	const responseInit = {
		headers: { 'set-cookie': setTheme(theme) },
	}
	if (redirectTo) {
		return redirect(redirectTo, responseInit)
	} else {
		return data({ result: submission.reply() }, responseInit)
	}
}

export function ThemeSwitch({
	userPreference,
}: {
	userPreference?: FormTheme | null
}) {
	const fetcher = useFetcher<typeof action>()
	const requestInfo = useRequestInfo()

	const [form] = useForm({
		id: 'theme-switch',
		lastResult: fetcher.data?.result,
	})

	const optimisticMode = useOptimisticThemeMode()
	const mode = optimisticMode ?? userPreference ?? 'system'
	const nextMode =
		mode === 'system' ? 'light' : mode === 'light' ? 'dark' : 'system'
	const modeLabel = {
		light: <Sun className="h-4 w-4" />,
		dark: <Moon className="h-4 w-4" />,
		system: <Laptop className="h-4 w-4" />,
	}

	return (
		<fetcher.Form
			method="POST"
			{...getFormProps(form)}
			action="/resources/theme-switch"
		>
			<ServerOnly>
				{() => (
					<input type="hidden" name="redirectTo" value={requestInfo.path} />
				)}
			</ServerOnly>
			<input type="hidden" name="theme" value={nextMode} />
			<div className="flex gap-2">
				<button
					type="submit"
					className="flex h-8 w-8 cursor-pointer items-center justify-center"
				>
					{modeLabel[mode]}
				</button>
			</div>
		</fetcher.Form>
	)
}

/**
 * If the user's changing their theme mode preference, this will return the
 * value it's being changed to. Returns undefined if there's a theme context override.
 */
export function useOptimisticThemeMode(): FormTheme | undefined {
	const contextTheme = useThemeContext()

	// If there's a context theme, don't use optimistic mode
	// as the context theme takes precedence
	if (contextTheme) {
		return undefined
	}

	const fetchers = useFetchers()
	const themeFetcher = fetchers.find(
		(f) => f.formAction === '/resources/theme-switch',
	)

	if (themeFetcher?.formData) {
		const submission = parseWithZod(themeFetcher.formData, {
			schema: ThemeFormSchema,
		})

		if (submission.status === 'success') {
			return submission.value.theme
		}
	}

	return undefined
}

/**
 * @returns the user's theme preference, or the client hint theme if the user
 * has not set a preference. Gives precedence to theme context when available.
 */
export function useTheme(): Theme {
	const contextTheme = useThemeContext()
	const hints = useHints()
	const requestInfo = useRequestInfo()
	const optimisticMode = useOptimisticThemeMode()

	// If there's a context theme override, use it directly (it's already resolved)
	if (contextTheme) {
		return contextTheme
	}

	// Otherwise use the existing logic
	if (optimisticMode) {
		return optimisticMode === 'system' ? hints.theme : optimisticMode
	}
	return requestInfo.userPrefs.theme ?? hints.theme
}
